// Lightweight plugin API inspired by Obsidian's plugin interfaces.
// It enables third-parties to extend the KnowledgeBase editor and preview
// with custom Markdown post processors, code block processors, and
// CodeMirror editor extensions – without disrupting existing behavior.

import { trackError, ErrorSeverity } from './PluginErrorTracker.js'

const listeners = new Set()

const notify = (type) => {
  for (const fn of listeners) {
    try {
      fn(type)
    } catch (err) {
      trackError({
        source: 'PluginAPI:listener',
        phase: 'notify',
        error: err,
        severity: ErrorSeverity.WARNING,
        context: { type }
      })
    }
  }
}

const state = {
  postProcessors: [], // [{ handler, sortOrder }]
  codeBlockProcessors: new Map(), // lang -> [{ handler, sortOrder }]
  editorExtensions: [], // CodeMirror extensions
  // Plugin lifecycle and settings
  plugins: new Map(), // pluginId -> { manifest, instance, settings, loaded }
  pluginSettings: new Map() // pluginId -> settings object
}
const sortByOrderAsc = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)

// ============================================================================
// PLUGIN LIFECYCLE MANAGEMENT
// ============================================================================

/**
 * Register a plugin with lifecycle hooks.
 * Similar to Obsidian's Plugin class pattern.
 * @param {Object} pluginDef - Plugin definition
 * @param {string} pluginDef.id - Unique plugin ID
 * @param {string} [pluginDef.name] - Display name
 * @param {string} [pluginDef.version] - Plugin version
 * @param {Function} [pluginDef.onLoad] - Called when plugin is loaded
 * @param {Function} [pluginDef.onUnload] - Called when plugin is unloaded
 * @param {Function} [pluginDef.onSettingsChange] - Called when settings change
 * @param {Object} [pluginDef.defaultSettings] - Default settings object
 * @returns {Object} Plugin controller with load/unload/settings methods
 */
export function registerPlugin(pluginDef) {
  if (!pluginDef || !pluginDef.id) {
    console.warn('[PluginAPI] Plugin registration requires an id')
    return null
  }

  const pluginId = String(pluginDef.id).trim()
  if (state.plugins.has(pluginId)) {
    console.warn(`[PluginAPI] Plugin "${pluginId}" is already registered`)
    return state.plugins.get(pluginId).controller
  }

  const entry = {
    manifest: {
      id: pluginId,
      name: pluginDef.name || pluginId,
      version: pluginDef.version || '1.0.0',
      description: pluginDef.description || ''
    },
    onLoad: pluginDef.onLoad || null,
    onUnload: pluginDef.onUnload || null,
    onSettingsChange: pluginDef.onSettingsChange || null,
    defaultSettings: pluginDef.defaultSettings || {},
    loaded: false,
    cleanupFunctions: [] // Track cleanup functions from registerXxx calls
  }

  // Initialize settings from defaults
  if (!state.pluginSettings.has(pluginId)) {
    state.pluginSettings.set(pluginId, { ...entry.defaultSettings })
  }

  /**
   * Plugin controller returned to the registering code
   */
  const controller = {
    get id() {
      return pluginId
    },
    get manifest() {
      return { ...entry.manifest }
    },
    get isLoaded() {
      return entry.loaded
    },

    /**
     * Load the plugin (calls onLoad hook)
     */
    async load() {
      if (entry.loaded) return
      try {
        if (entry.onLoad) {
          await entry.onLoad(this)
        }
        entry.loaded = true
        notify('plugin:loaded')
      } catch (err) {
        trackError({
          source: pluginId,
          phase: 'load',
          error: err,
          severity: ErrorSeverity.ERROR
        })
        throw err
      }
    },

    /**
     * Unload the plugin (calls onUnload hook and cleans up registrations)
     */
    async unload() {
      if (!entry.loaded) return
      try {
        // Call cleanup functions for registered processors
        for (const cleanup of entry.cleanupFunctions) {
          try {
            cleanup()
          } catch (e) {
            trackError({
              source: pluginId,
              phase: 'cleanup',
              error: e,
              severity: ErrorSeverity.WARNING
            })
          }
        }
        entry.cleanupFunctions = []

        if (entry.onUnload) {
          await entry.onUnload()
        }
        entry.loaded = false
        notify('plugin:unloaded')
      } catch (err) {
        trackError({
          source: pluginId,
          phase: 'unload',
          error: err,
          severity: ErrorSeverity.ERROR
        })
        throw err
      }
    },

    /**
     * Get plugin settings
     * @returns {Object}
     */
    loadData() {
      return { ...(state.pluginSettings.get(pluginId) || entry.defaultSettings) }
    },

    /**
     * Save plugin settings
     * @param {Object} data - Settings to save
     */
    async saveData(data) {
      const oldSettings = state.pluginSettings.get(pluginId) || {}
      const newSettings = { ...oldSettings, ...data }
      state.pluginSettings.set(pluginId, newSettings)

      // Call settings change hook
      if (entry.onSettingsChange) {
        try {
          await entry.onSettingsChange(newSettings, oldSettings)
        } catch (err) {
          trackError({
            source: pluginId,
            phase: 'settingsChange',
            error: err,
            severity: ErrorSeverity.WARNING
          })
        }
      }

      notify('plugin:settingsChanged')
      return newSettings
    },

    /**
     * Register a markdown post processor with automatic cleanup tracking
     */
    registerMarkdownPostProcessor(handler, options = {}) {
      const cleanup = registerMarkdownPostProcessor(handler, options)
      entry.cleanupFunctions.push(cleanup)
      return cleanup
    },

    /**
     * Register a code block processor with automatic cleanup tracking
     */
    registerMarkdownCodeBlockProcessor(lang, handler, options = {}) {
      const cleanup = registerMarkdownCodeBlockProcessor(lang, handler, options)
      entry.cleanupFunctions.push(cleanup)
      return cleanup
    },

    /**
     * Register an editor extension with automatic cleanup tracking
     */
    registerEditorExtension(extension) {
      const cleanup = registerEditorExtension(extension)
      entry.cleanupFunctions.push(cleanup)
      return cleanup
    }
  }

  entry.controller = controller
  state.plugins.set(pluginId, entry)
  notify('plugin:registered')

  return controller
}

/**
 * Unregister a plugin completely
 * @param {string} pluginId - The plugin ID
 */
export async function unregisterPlugin(pluginId) {
  const entry = state.plugins.get(pluginId)
  if (!entry) return

  // Unload if loaded
  if (entry.loaded && entry.controller) {
    await entry.controller.unload()
  }

  state.plugins.delete(pluginId)
  state.pluginSettings.delete(pluginId)
  notify('plugin:unregistered')
}

/**
 * Get a registered plugin controller
 * @param {string} pluginId - The plugin ID
 * @returns {Object|null}
 */
export function getPlugin(pluginId) {
  const entry = state.plugins.get(pluginId)
  return entry ? entry.controller : null
}

/**
 * Get all registered plugin IDs
 * @returns {string[]}
 */
export function getRegisteredPluginIds() {
  return Array.from(state.plugins.keys())
}

/**
 * Get all loaded plugins
 * @returns {Object[]} Array of plugin controllers
 */
export function getLoadedPlugins() {
  return Array.from(state.plugins.values())
    .filter((entry) => entry.loaded)
    .map((entry) => entry.controller)
}

// ============================================================================
// REGISTRY CHANGE LISTENERS
// ============================================================================

export function onPluginRegistryChange(cb) {
  if (typeof cb !== 'function') return () => {}
  listeners.add(cb)
  return () => listeners.delete(cb)
}

// Markdown post processors (run after HTML is rendered)
export function registerMarkdownPostProcessor(handler, options = {}) {
  if (typeof handler !== 'function') return () => {}
  const entry = { handler, sortOrder: options.sortOrder ?? options.priority ?? 0 }
  state.postProcessors.push(entry)
  state.postProcessors.sort(sortByOrderAsc)
  notify('postProcessor:registered')
  return () => unregisterMarkdownPostProcessor(handler)
}

export function unregisterMarkdownPostProcessor(handler) {
  const idx = state.postProcessors.findIndex((e) => e.handler === handler)
  if (idx >= 0) state.postProcessors.splice(idx, 1)
  notify('postProcessor:unregistered')
}

export function getMarkdownPostProcessors() {
  return state.postProcessors.slice()
}

// Markdown fenced code block processors (```lang)
export function registerMarkdownCodeBlockProcessor(lang, handler, options = {}) {
  const key = String(lang || '')
    .toLowerCase()
    .trim()
  if (!key || typeof handler !== 'function') return () => {}
  if (!state.codeBlockProcessors.has(key)) state.codeBlockProcessors.set(key, [])
  const entry = { handler, sortOrder: options.sortOrder ?? options.priority ?? 0 }
  const list = state.codeBlockProcessors.get(key)
  list.push(entry)
  list.sort(sortByOrderAsc)
  notify('codeBlockProcessor:registered')
  return () => unregisterMarkdownCodeBlockProcessor(lang, handler)
}

export function unregisterMarkdownCodeBlockProcessor(lang, handler) {
  const key = String(lang || '')
    .toLowerCase()
    .trim()
  if (!state.codeBlockProcessors.has(key)) return
  const list = state.codeBlockProcessors.get(key)
  const idx = list.findIndex((e) => e.handler === handler)
  if (idx >= 0) list.splice(idx, 1)
  notify('codeBlockProcessor:unregistered')
}

export function getMarkdownCodeBlockProcessors(lang) {
  const key = String(lang || '')
    .toLowerCase()
    .trim()
  return (state.codeBlockProcessors.get(key) || []).slice()
}

// CodeMirror editor extensions
export function registerEditorExtension(extension) {
  if (!extension) return () => {}
  state.editorExtensions.push(extension)
  notify('editorExtension:registered')
  return () => unregisterEditorExtension(extension)
}

export function unregisterEditorExtension(extension) {
  const idx = state.editorExtensions.indexOf(extension)
  if (idx >= 0) state.editorExtensions.splice(idx, 1)
  notify('editorExtension:unregistered')
}

export function getEditorExtensions() {
  return state.editorExtensions.slice()
}

// Expose a richer context similar to Obsidian's processors
export function createProcessorContext(base = {}) {
  return {
    ...base,
    // Timers
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: (id) => clearInterval(id),
    // Source info (if available)
    sourcePath: base.sourcePath || null,
    frontmatter: base.frontmatter || {},
    // Component lifecycle helpers
    addCleanup: base.addCleanup || (() => {}),
    // DOM helpers
    createEl: (tag, attrs = {}) => {
      const el = document.createElement(tag)
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'cls' || key === 'className') {
          el.className = Array.isArray(value) ? value.join(' ') : value
        } else if (key === 'text') {
          el.textContent = value
        } else if (key.startsWith('on') && typeof value === 'function') {
          el.addEventListener(key.slice(2).toLowerCase(), value)
        } else {
          el.setAttribute(key, value)
        }
      })
      return el
    },
    createSpan: (attrs = {}) => {
      const el = document.createElement('span')
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'cls' || key === 'className') {
          el.className = Array.isArray(value) ? value.join(' ') : value
        } else if (key === 'text') {
          el.textContent = value
        } else {
          el.setAttribute(key, value)
        }
      })
      return el
    },
    createDiv: (attrs = {}) => {
      const el = document.createElement('div')
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'cls' || key === 'className') {
          el.className = Array.isArray(value) ? value.join(' ') : value
        } else if (key === 'text') {
          el.textContent = value
        } else {
          el.setAttribute(key, value)
        }
      })
      return el
    }
  }
}

export default {
  // Plugin lifecycle
  registerPlugin,
  unregisterPlugin,
  getPlugin,
  getRegisteredPluginIds,
  getLoadedPlugins,
  // Registry listeners
  onPluginRegistryChange,
  // Markdown processors
  registerMarkdownPostProcessor,
  unregisterMarkdownPostProcessor,
  getMarkdownPostProcessors,
  registerMarkdownCodeBlockProcessor,
  unregisterMarkdownCodeBlockProcessor,
  getMarkdownCodeBlockProcessors,
  // Editor extensions
  registerEditorExtension,
  unregisterEditorExtension,
  getEditorExtensions,
  // Context
  createProcessorContext
}
